import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { useUserOptional } from "@/auth/UserContext";
import { api } from "@/services/api";

/** Map of feature key → enabled. Unknown keys default to false. */
export type FeatureFlags = Record<string, boolean>;

export const AIS_VESSEL_TRACKING = "ais_vessel_tracking";
export const RATE_LOOKUP = "rate_lookup";
export const SCHEDULE_LOOKUP = "schedule_lookup";
export const KAI_ENABLED = "kai_enabled";
/** Gates the Home Kai briefing widget and the entity-summary API it calls. */
export const KAI_INSIGHTS = "kai_insights";
/** Gates Kai-generated, data-backed welcome prompts on the new-chat screen. */
export const KAI_WELCOME_PROMPTS = "kai_welcome_prompts";
/** Gates the Microsoft Graph email integration (Admin → Integrations). */
export const MICROSOFT_GRAPH_EMAIL = "microsoft_graph_email";
/** Gates per-booking carrier container tracking on the booking detail page. */
export const CARRIER_TRACKING = "carrier_tracking";
/** Gates the Invoices reconciliation module (page, nav, API). */
export const INVOICE_RECONCILIATION = "invoice_reconciliation";
/** Gates the Desktop RPA agent integration (twido-agent management UI). */
export const DESKTOP_RPA = "desktop_rpa";

const DEFAULTS: FeatureFlags = {
  ais_vessel_tracking: false,
  rate_lookup: true,
  schedule_lookup: true,
  kai_enabled: true,
  kai_insights: false,
  kai_welcome_prompts: false,
  microsoft_graph_email: false,
  carrier_tracking: true,
  invoice_reconciliation: false,
  desktop_rpa: false,
};

interface FeatureFlagsContextValue {
  flags: FeatureFlags;
  loading: boolean;
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(
  null,
);

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const user = useUserOptional();
  const clientKey = user?.profile?.client_key ?? null;
  const isAuthenticated = user?.isAuthenticated ?? false;
  const isAuthLoading = user?.isAuthLoading ?? true;
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (isAuthLoading) return;
    if (!isAuthenticated) {
      setFlags(DEFAULTS);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await api.getResolvedFeatureFlags();
      setFlags({ ...DEFAULTS, ...data });
    } catch (err) {
      console.warn("Failed to fetch feature flags, using defaults:", err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    void refresh();
  }, [refresh, clientKey]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, refresh }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error("useFeatureFlags must be used within FeatureFlagsProvider");
  }
  return ctx;
}

export function useFeatureEnabled(key: string): boolean {
  const { flags } = useFeatureFlags();
  return flags[key] ?? false;
}
